import React, { useState, useCallback } from 'react';
import { useMsal } from '@azure/msal-react';
import { loginRequest } from '../../auth/msalConfig';
import { MdErrorOutline } from '../../utils/icons';
import omnilinqLogo from '../../assets/omnilinq_logo.png';
import './Login.css';

const LoginPage: React.FC = () => {
  const { instance } = useMsal();
  const [alert, setAlert] = useState<{ message: string } | null>(null);
  const [loading, setLoading] = useState(false);

  const handleMicrosoftLogin = useCallback(async () => {
    setAlert(null);
    setLoading(true);
    try {
      await instance.loginRedirect(loginRequest);
    } catch (error: any) {
      setLoading(false);
      console.error('[Login] loginRedirect error:', error);
      setAlert({ message: 'Microsoft sign-in failed. Please try again.' });
    }
  }, [instance]);

  return (
    <div className="login-root">

      <header className="topbar">
        <div className="brand-mark">
          <span className="dot" />
          <span>BUNZL · TECHNOLOGY SERVICES</span>
        </div>
        <nav className="topbar-nav">
          <span className="status-pill">
            <span className="pulse" />
            All systems operational
          </span>
        </nav>
      </header>

      <main className="shell">

        <section className="brand-col">
          <img src={omnilinqLogo} alt="OmniLinQ" className="brand-logo" />
          <h1 className="brand-heading">
            One link.
            <span className="accent">Infinite intelligence.</span>
          </h1>
          <p className="brand-sub">
            Your unified gateway to Bunzl's AI agents — product support, ticket triage,
            knowledge retrieval, and more. Sign in with your corporate account to continue.
          </p>
        </section>

        <section className="auth-col">
          <div className="card">

            {alert && (
              <div className="login-alert" role="alert" aria-live="assertive">
                <MdErrorOutline />
                <span>{alert.message}</span>
              </div>
            )}

            <h2 className="card-heading">Welcome back</h2>
            <p className="card-sub">Sign in with your Bunzl corporate account</p>

            <button
              className={`sso-btn${loading ? ' loading' : ''}`}
              type="button"
              onClick={handleMicrosoftLogin}
              disabled={loading}
            >
              <svg width="18" height="18" viewBox="0 0 21 21" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                <rect x="1"  y="1"  width="9" height="9" fill="#F25022" />
                <rect x="11" y="1"  width="9" height="9" fill="#7FBA00" />
                <rect x="1"  y="11" width="9" height="9" fill="#00A4EF" />
                <rect x="11" y="11" width="9" height="9" fill="#FFB900" />
              </svg>
              {loading ? 'Redirecting…' : 'Continue with Microsoft'}
              {!loading && (
                <svg className="btn-arrow" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <line x1="5" y1="12" x2="19" y2="12" />
                  <polyline points="12 5 19 12 12 19" />
                </svg>
              )}
            </button>

            <p className="auth-note">
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                <path d="M7 11V7a5 5 0 0 1 10 0v4" />
              </svg>
              Authenticated via Microsoft Entra ID · OAuth 2.0 / OIDC
            </p>

          </div>
        </section>

      </main>

      <footer className="page-foot">
        <div>© 2026 Bunzl plc · OmniLinQ v1.0</div>
      </footer>

    </div>
  );
};

export default LoginPage;
